import { useRouter } from 'vue-router'
import { toast } from 'vue-sonner'
import { usePostHog } from '../usePostHog.js'

/**
 * Composable pour gérer les actions de QuoteDetailPage
 * Limite: 80 lignes max
 */
export function useQuoteDetailPageActions(quoteId, quote, callbacks = {}) {
  const { deleteQuote, updateStatus, loadQuote, startGlobalCall, generateAndSendQuote, generatePDFPreview } = callbacks
  const router = useRouter()
  const { captureException } = usePostHog()

  const startCall = (direction = 'sortant') => {
    if (quote.value) {
      startGlobalCall(quoteId, quote.value, {
        updateStatus,
        loadQuote,
        loadHistory: callbacks.loadHistory,
        loadComments: callbacks.loadComments,
        generateAndSendQuote
      }, direction)
    }
  }

  const createDatesHandlers = (isEditing, editedQuote) => {
    // Jours précis et périodes approximatives sont exclusifs : renseigner l'un vide l'autre.
    const handleDatesDisponibilitesChange = (dates) => {
      if (isEditing.value) {
        editedQuote.value.dates_disponibilites = dates
        if (dates.length > 0) {
          editedQuote.value.dates_periodes = []
        }
      }
    }

    const handleDatesPeriodesChange = (periodes) => {
      if (isEditing.value) {
        editedQuote.value.dates_periodes = periodes
        if (periodes.length > 0) {
          editedQuote.value.dates_disponibilites = []
        }
      }
    }

    const handleDatesInconnuesChange = (value) => {
      if (isEditing.value) {
        editedQuote.value.dates_inconnues = value
        if (value) {
          editedQuote.value.dates_disponibilites = []
          editedQuote.value.dates_periodes = []
        }
      }
    }

    return {
      handleDatesDisponibilitesChange,
      handleDatesPeriodesChange,
      handleDatesInconnuesChange
    }
  }

  const viewQuotePDF = async () => {
    if (quote.value?.quote_pdf_url) {
      window.open(quote.value.quote_pdf_url, '_blank')
      return
    }
    
    if (generatePDFPreview && quote.value?.quote_amount) {
      await generatePDFPreview(quote.value.quote_amount, false)
    }
  }

  const handleDeleteQuote = async () => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return
    }
    
    try {
      await deleteQuote()
      toast.success('Demande supprimée avec succès')
      router.push('/')
    } catch (err) {
      // Capture error in PostHog with context
      captureException(err, {
        error_type: 'quote_action_error',
        quote_id: quoteId,
        action_type: 'delete',
        error_message: err.message || 'Erreur lors de la suppression'
      })
      
      console.error('Erreur lors de la suppression:', err)
    }
  }

  const restoreFromTrash = async () => {
    if (!confirm('Êtes-vous sûr de vouloir restaurer cette demande depuis la corbeille ?')) {
      return
    }
    try {
      await updateStatus('demande_devis')
      toast.success('Demande restaurée avec succès')
      await loadQuote()
    } catch (err) {
      // Capture error in PostHog with context
      captureException(err, {
        error_type: 'quote_action_error',
        quote_id: quoteId,
        action_type: 'restore_from_trash',
        error_message: err.message || 'Erreur lors de la restauration'
      })
      
      toast.error('Erreur lors de la restauration')
      console.error(err)
    }
  }

  return {
    startCall,
    createDatesHandlers,
    viewQuotePDF,
    handleDeleteQuote,
    restoreFromTrash
  }
}
